import { UnitLabels } from 'Util/UnitOfMeasurementUtils';

export type WeightUnit = 'kg' | 't' | 'lb' | 'ton_us';

/**
 * Largest exact-weight value that can be persisted. `ExactWeight` is stored as
 * a Postgres `numeric(10,3)` column (in the entered unit), so the maximum
 * storable magnitude is 9,999,999.999. Anything larger overflows the column and
 * fails the booking save, so the wizard must block it before submission.
 */
export const EXACT_WEIGHT_MAX_STORABLE = 9_999_999.999;

const TONNES_TO_KG = 1000;
const POUNDS_TO_KG = 0.45359237;
const US_TONS_TO_KG = 907.18474; // 2000 lb × POUNDS_TO_KG

const TO_KG: Record<WeightUnit, number> = {
	kg: 1,
	t: TONNES_TO_KG,
	lb: POUNDS_TO_KG,
	ton_us: US_TONS_TO_KG,
};

const WEIGHT_UNIT_LABELS: Record<WeightUnit, string> = {
	kg: UnitLabels.mass.kilograms,
	t: UnitLabels.mass.tonne,
	lb: UnitLabels.mass.pounds,
	ton_us: UnitLabels.mass.usTon,
};

export const isWeightUnit = (value: unknown): value is WeightUnit => {
	return typeof value === 'string' && value in TO_KG;
};

export const parseWeightUnit = (value: string | null | undefined, fallback: WeightUnit = 'kg'): WeightUnit => {
	return isWeightUnit(value) ? value : fallback;
};

export const weightUnitLabel = (unit: WeightUnit): string => WEIGHT_UNIT_LABELS[unit];

export const convertWeight = (value: number, from: WeightUnit, to: WeightUnit): number => {
	if (from === to) return value;
	const kg = value * TO_KG[from];
	return kg / TO_KG[to];
};

export const toCanonicalKg = (value: number, unit: WeightUnit): number => {
	return value * TO_KG[unit];
};

/**
 * Round a converted weight value for display: tonnes get up to 2 decimal
 * places (trailing zeros stripped), other units (kg/lb/ton_us) are rounded to
 * whole numbers. Returns a number so callers can format alongside their own
 * unit label or use it directly.
 */
export const roundWeightForUnit = (value: number, unit: WeightUnit): number => {
	if (unit === 't') {
		return Math.round(value * 100) / 100;
	}
	return Math.round(value);
};

export const formatWeight = (value: number, storedUnit: WeightUnit, displayUnit: WeightUnit): string => {
	const converted = convertWeight(value, storedUnit, displayUnit);
	return `${roundWeightForUnit(converted, displayUnit)} ${WEIGHT_UNIT_LABELS[displayUnit]}`;
};

export interface WeightBandCandidate {
	id: string;
	value: number | null | undefined;
}

/**
 * Pick the weight band that an exact weight falls into.
 *
 * Semantics: smallest band where `value >= exactKg`. If the entered weight
 * exceeds every band, returns the largest band so very-heavy vehicles still
 * get a valid pricing tier. Bands with no `value` are ignored.
 */
export const pickWeightBand = (exactKg: number, candidates: WeightBandCandidate[]): string | null => {
	const sorted = candidates
		.filter((c): c is { id: string; value: number } => c.value != null && !Number.isNaN(c.value))
		.sort((a, b) => a.value - b.value);

	if (sorted.length === 0) return null;

	const fits = sorted.find(c => c.value >= exactKg);
	return (fits ?? sorted[sorted.length - 1]).id;
};
